import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  getAuth,
  signInAnonymously,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  type Auth,
} from 'firebase/auth';

/**
 * Client-side mirror of the server's auth seam.
 *
 * With VITE_FIREBASE_* set, this is real Firebase. Without it, we mint the dev
 * tokens the server's DevAuthProvider accepts (`dev:<uid>` / `guest:<uid>`), so
 * the whole game is playable before any credentials exist.
 *
 * The rest of the app only ever calls `getIdToken()` and never learns which
 * mode is live.
 */

const cfg = {
  apiKey: import.meta.env['VITE_FIREBASE_API_KEY'] as string | undefined,
  authDomain: import.meta.env['VITE_FIREBASE_AUTH_DOMAIN'] as string | undefined,
  projectId: import.meta.env['VITE_FIREBASE_PROJECT_ID'] as string | undefined,
  appId: import.meta.env['VITE_FIREBASE_APP_ID'] as string | undefined,
};

export const firebaseEnabled =
  Boolean(cfg.apiKey) && Boolean(cfg.authDomain) && Boolean(cfg.projectId) && Boolean(cfg.appId);

let app: FirebaseApp | undefined;
let auth: Auth | undefined;

if (firebaseEnabled) {
  app = initializeApp({
    apiKey: cfg.apiKey as string,
    authDomain: cfg.authDomain as string,
    projectId: cfg.projectId as string,
    appId: cfg.appId as string,
  });
  auth = getAuth(app);
}

/* -------------------------------------------------------------------------- */
/* Dev mode                                                                    */
/* -------------------------------------------------------------------------- */

const DEV_TOKEN_KEY = 'mr:devToken';
const DEV_NAME_KEY = 'mr:devName';

function randomId(): string {
  return Math.random().toString(36).slice(2, 10);
}

export interface AuthUser {
  readonly displayName: string;
  readonly isAnonymous: boolean;
  /** The bearer token handed to the server on the socket handshake. */
  readonly token: string;
}

function devUser(token: string, displayName: string, isAnonymous: boolean): AuthUser {
  localStorage.setItem(DEV_TOKEN_KEY, token);
  localStorage.setItem(DEV_NAME_KEY, displayName);
  return { token, displayName, isAnonymous };
}

/** Restores a session from a previous visit, if any. */
export async function restoreSession(): Promise<AuthUser | null> {
  if (!firebaseEnabled) {
    const token = localStorage.getItem(DEV_TOKEN_KEY);
    const name = localStorage.getItem(DEV_NAME_KEY);
    if (!token || !name) return null;
    return { token, displayName: name, isAnonymous: token.startsWith('guest:') };
  }

  const a = auth;
  if (!a) return null;
  return new Promise((resolve) => {
    const unsub = a.onAuthStateChanged((u) => {
      unsub();
      if (!u) {
        resolve(null);
        return;
      }
      void u.getIdToken().then((token) =>
        resolve({
          token,
          displayName: u.displayName ?? u.email?.split('@')[0] ?? 'Player',
          isAnonymous: u.isAnonymous,
        }),
      );
    });
  });
}

export async function signInWithEmail(email: string, password: string): Promise<AuthUser> {
  if (!firebaseEnabled) {
    const name = email.split('@')[0] || 'Player';
    return devUser(`dev:${name}-${randomId()}:${name}`, name, false);
  }
  const a = auth;
  if (!a) throw new Error('Firebase not initialised');
  const cred = await signInWithEmailAndPassword(a, email, password);
  return {
    token: await cred.user.getIdToken(),
    displayName: cred.user.displayName ?? email.split('@')[0] ?? 'Player',
    isAnonymous: false,
  };
}

export async function signUpWithEmail(email: string, password: string): Promise<AuthUser> {
  if (!firebaseEnabled) return signInWithEmail(email, password);
  const a = auth;
  if (!a) throw new Error('Firebase not initialised');
  const cred = await createUserWithEmailAndPassword(a, email, password);
  return {
    token: await cred.user.getIdToken(),
    displayName: email.split('@')[0] ?? 'Player',
    isAnonymous: false,
  };
}

export async function signInWithGoogle(): Promise<AuthUser> {
  if (!firebaseEnabled) {
    const name = `Player${randomId().slice(0, 4)}`;
    return devUser(`dev:${name}:${name}`, name, false);
  }
  const a = auth;
  if (!a) throw new Error('Firebase not initialised');
  const cred = await signInWithPopup(a, new GoogleAuthProvider());
  return {
    token: await cred.user.getIdToken(),
    displayName: cred.user.displayName ?? 'Player',
    isAnonymous: false,
  };
}

export async function playAsGuest(): Promise<AuthUser> {
  if (!firebaseEnabled) {
    const id = randomId();
    return devUser(`guest:${id}`, `Guest-${id.slice(0, 4).toUpperCase()}`, true);
  }
  const a = auth;
  if (!a) throw new Error('Firebase not initialised');
  const cred = await signInAnonymously(a);
  return {
    token: await cred.user.getIdToken(),
    displayName: 'Guest',
    isAnonymous: true,
  };
}

export async function signOut(): Promise<void> {
  localStorage.removeItem(DEV_TOKEN_KEY);
  localStorage.removeItem(DEV_NAME_KEY);
  if (firebaseEnabled && auth) await fbSignOut(auth);
}
